import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule, Router } from '@angular/router';
import { Subscription } from 'rxjs';

import { MenubarModule } from 'primeng/menubar';
import { ButtonModule } from 'primeng/button';
import { MenuModule } from 'primeng/menu';
import { BadgeModule } from 'primeng/badge';
import { AvatarModule } from 'primeng/avatar';
import { TooltipModule } from 'primeng/tooltip';
import { MenuItem } from 'primeng/api';

import { TranslocoModule, TranslocoService } from '@ngneat/transloco';

import { AuthService } from '../core/services/auth.service';
import { ThemeService } from '../shared/services/theme.service';
import { User } from '../core/models/user.model';
import { environment } from '../../environments/environment';

@Component({
  selector: 'app-layout',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    MenubarModule,
    ButtonModule,
    MenuModule,
    BadgeModule,
    AvatarModule,
    TooltipModule,
    TranslocoModule
  ],
  templateUrl: './layout.component.html',
  styleUrl: './layout.component.scss'
})
export class LayoutComponent implements OnInit, OnDestroy {
  currentUser: User | null = null;
  menuItems: MenuItem[] = [];
  userMenuItems: MenuItem[] = [];
  notificationCount = 0;
  currentLang = 'de';
  appVersion = environment.version;
  private subscriptions = new Subscription();

  constructor(
    private authService: AuthService,
    public themeService: ThemeService,
    private router: Router,
    private translocoService: TranslocoService
  ) {}

  ngOnInit(): void {
    // Initialize current language
    this.currentLang = this.translocoService.getActiveLang();
    
    this.subscriptions.add(
      this.authService.currentUser$.subscribe(user => {
        this.currentUser = user;
        this.setupMenuItems();
      })
    );
    
    // Subscribe to language changes
    this.subscriptions.add(
      this.translocoService.langChanges$.subscribe(lang => {
        this.currentLang = lang;
        this.setupMenuItems(); // Update menu items when language changes
      })
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  private setupMenuItems(): void {
    if (!this.currentUser) return;

    // Use the active translation to set up menu items with proper fallback
    const t = (key: string, fallback: string) => {
      const translation = this.translocoService.translate(key);
      // Return fallback if translation key is returned unchanged or empty
      return (translation && translation !== key) ? translation : fallback;
    };
    
    // Main navigation menu
    this.menuItems = [
      {
        label: t('navigation.dashboard', 'Dashboard'),
        icon: 'pi pi-home',
        routerLink: '/dashboard'
      },
      {
        label: t('navigation.orders', 'Orders'),
        icon: 'pi pi-shopping-cart', 
        routerLink: '/orders'
      }
    ];

    // Add admin/internal specific menu items
    if (this.currentUser.role === 'admin' || this.currentUser.role === 'internal') {
      this.menuItems.push({
        label: t('navigation.users', 'Users'),
        icon: 'pi pi-users',
        routerLink: '/users'
      });
    }

    // Add admin-only menu items
    if (this.currentUser.role === 'admin') {
      this.menuItems.push({
        label: t('navigation.admin', 'Admin'),
        icon: 'pi pi-cog',
        items: [
          {
            label: t('navigation.userManagement', 'User Management'),
            icon: 'pi pi-users',
            routerLink: '/admin/users'
          }
        ]
      });
    }

    // User dropdown menu
    this.userMenuItems = [
      {
        label: t('navigation.logout', 'Logout'),
        icon: 'pi pi-sign-out',
        command: () => this.logout()
      }
    ];
  }

  getUserInitials(): string {
    if (!this.currentUser) return '';
    const first = this.currentUser.firstName.charAt(0).toUpperCase();
    const last = this.currentUser.lastName.charAt(0).toUpperCase();
    return `${first}${last}`;
  }

  getUserColor(): string {
    if (!this.currentUser) return '#6c757d';
    
    const colors = {
      'customer': '#007bff',
      'internal': '#28a745', 
      'admin': '#dc3545'
    };
    
    return colors[this.currentUser.role] || '#6c757d';
  }

  getRoleDisplay(role?: string): string {
    if (!role) return '';
    
    // Use translate with fallback to handle missing translations gracefully
    const translation = this.translocoService.translate(`roles.${role}`);
    return translation !== `roles.${role}` ? translation : role.charAt(0).toUpperCase() + role.slice(1);
  }

  toggleNotifications(): void {
    // TODO: Implement notifications panel
    console.log('Toggle notifications');
  }

  toggleTheme(): void {
    this.themeService.toggleTheme();
  }

  toggleLanguage(): void {
    const newLang = this.currentLang === 'en' ? 'de' : 'en';
    this.translocoService.setActiveLang(newLang);
  }

  navigateToDashboard(): void {
    this.router.navigate(['/dashboard']);
  }

  logout(): void {
    this.authService.logout();
  }
}